import { useEffect, useState, type MouseEvent } from "react";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeId, setActiveId] = useState<string>("");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { label: "Services", href: "#services" },
    { label: "Systems", href: "#systems" },
    { label: "How it works", href: "#how" },
    { label: "Reviews", href: "#reviews" },
    { label: "Financing", href: "#financing" },
    { label: "Service Area", href: "#area" },
  ];

  const getHeaderOffset = () => {
    const el = document.querySelector("header");
    return (el?.getBoundingClientRect().height ?? 108) + 12;
  };

  const handleNavClick = (e: MouseEvent<HTMLAnchorElement>, href: string) => {
    if (!href.startsWith("#") || href === "#") return;
    const target = document.querySelector(href) as HTMLElement | null;
    if (!target) return;
    e.preventDefault();
    const top = target.getBoundingClientRect().top + window.scrollY - getHeaderOffset();
    window.scrollTo({ top, behavior: "smooth" });
    setOpen(false);
  };

  useEffect(() => {
    const ids = links.map((l) => l.href.slice(1));
    const sections = ids
      .map((id) => document.getElementById(id))
      .filter((n): n is HTMLElement => !!n);
    if (!sections.length) return;

    const computeActive = () => {
      const probe = getHeaderOffset() + 24;
      let current = "";
      for (const s of sections) {
        const rect = s.getBoundingClientRect();
        if (rect.top - probe <= 0) current = s.id;
        else break;
      }
      // fallback: if none passed the probe yet, keep empty until first section reached
      const nearBottom = window.innerHeight + window.scrollY >= document.body.scrollHeight - 4;
      if (nearBottom) current = sections[sections.length - 1].id;
      setActiveId(current);
    };

    computeActive();
    window.addEventListener("scroll", computeActive, { passive: true });
    window.addEventListener("resize", computeActive);
    return () => {
      window.removeEventListener("scroll", computeActive);
      window.removeEventListener("resize", computeActive);
    };
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-shadow duration-300 ${
        scrolled ? "shadow-[0_2px_12px_rgba(11,27,59,0.08)]" : ""
      }`}
    >
      {/* Layer 1 — Utility bar */}
      <div className="w-full bg-white border-b border-slate-200/80">
        <div className="max-w-[1400px] mx-auto flex items-center justify-between px-6 md:px-10 h-[56px]">
          <a href="#" className="flex items-center gap-2.5 cursor-pointer">
            <div className="w-10 h-10 rounded-lg bg-brand-blue flex items-center justify-center">
              <i className="ri-snowflake-line text-white text-xl"></i>
            </div>
            <div className="leading-none">
              <div
                className="font-display font-800 text-[18px] text-brand-navy"
                style={{ fontWeight: 800 }}
              >
                Summit Home Services
              </div>
              <div className="text-[10px] tracking-[0.18em] text-brand-navy/60 mt-1">
                HEATING · COOLING · AIR QUALITY
              </div>
            </div>
          </a>

          <div className="hidden md:flex items-center gap-6">
            <div className="flex items-center gap-2 text-[12px] text-brand-navy/70 font-medium">
              <i className="ri-shield-check-fill text-brand-blue"></i>
              Licensed & Insured · TACLA #48120
            </div>
            <div className="flex items-center gap-2 text-[12px] text-brand-navy/70 font-medium">
              <i className="ri-star-fill text-brand-ember"></i>
              4.9 · 2,400+ Reviews
            </div>
            <a
              href="tel:5125550187"
              className="flex items-center gap-2 text-brand-navy font-bold text-[15px] whitespace-nowrap cursor-pointer hover:text-brand-blue transition-colors"
            >
              <span className="w-8 h-8 rounded-full bg-brand-blue/10 flex items-center justify-center">
                <i className="ri-phone-fill text-brand-blue text-sm"></i>
              </span>
              (512) 555-0187
            </a>
          </div>

          <button
            onClick={() => setOpen(!open)}
            className="lg:hidden w-10 h-10 rounded-md bg-white border border-slate-200 flex items-center justify-center cursor-pointer"
          >
            <i className={`text-xl ${open ? "ri-close-line" : "ri-menu-line"}`}></i>
          </button>
        </div>
      </div>

      {/* Layer 2 — Main navigation */}
      <div className="w-full bg-brand-navy hidden lg:block">
        <div className="max-w-[1400px] mx-auto flex items-center justify-between px-6 md:px-10 h-[52px]">
          <nav className="flex items-center gap-1">
            {links.map((l) => {
              const id = l.href.slice(1);
              const isActive = activeId === id;
              return (
                <a
                  key={l.label}
                  href={l.href}
                  onClick={(e) => handleNavClick(e, l.href)}
                  className={`relative text-[14px] font-semibold px-4 py-2 rounded-md cursor-pointer whitespace-nowrap transition-colors ${
                    isActive ? "text-white" : "text-white/75 hover:text-white"
                  }`}
                >
                  {l.label}
                  <span
                    className="pointer-events-none absolute left-4 right-4 -bottom-[2px] h-[2px] rounded-full bg-white origin-center"
                    style={{
                      transform: `scaleX(${isActive ? 1 : 0})`,
                      opacity: isActive ? 1 : 0,
                      transition: "transform 350ms cubic-bezier(0.16,1,0.3,1), opacity 300ms ease-out",
                    }}
                  ></span>
                </a>
              );
            })}
          </nav>

          <div className="flex items-center gap-3">
            <a
              href="#emergency"
              className="inline-flex items-center gap-2 text-white/85 hover:text-white text-[13px] font-semibold whitespace-nowrap cursor-pointer"
            >
              <i className="ri-alarm-warning-fill text-brand-ember"></i>
              24/7 Emergency
            </a>
            <a
              href="#book"
              onClick={(e) => handleNavClick(e, "#book")}
              className="inline-flex items-center gap-2 bg-brand-ember hover:bg-brand-emberDark text-white text-[13px] font-bold px-5 py-2 rounded-md whitespace-nowrap cursor-pointer transition-colors"
            >
              <i className="ri-calendar-check-line"></i>
              Book Service
            </a>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden bg-white border-t border-slate-100 px-6 py-4 flex flex-col gap-3">
          {links.map((l) => {
            const id = l.href.slice(1);
            const isActive = activeId === id;
            return (
              <a
                key={l.label}
                href={l.href}
                onClick={(e) => handleNavClick(e, l.href)}
                className={`py-1 cursor-pointer font-medium ${isActive ? "text-brand-blue" : "text-brand-navy"}`}
              >
                {l.label}
              </a>
            );
          })}
          <a
            href="#book"
            onClick={(e) => handleNavClick(e, "#book")}
            className="mt-2 inline-flex items-center justify-center gap-2 bg-brand-blue text-white font-semibold px-5 py-2.5 rounded-md whitespace-nowrap cursor-pointer"
          >
            Book Service
          </a>
        </div>
      )}
    </header>
  );
}